import gql from 'graphql-tag';
import _ from 'underscore';

import {assert} from 'Asserts';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DataFrameValueAccessor} from 'data-frames/util/createAccessors';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import * as LinearRegression from 'ui-library/charting/statistics/regression/LinearRegression';
import * as LogisticRegression from 'ui-library/charting/statistics/regression/LogisticRegression';
import * as QuadraticRegression from 'ui-library/charting/statistics/regression/QuadraticRegression';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {AnalysisStatus, SuccessfulAnalysis} from 'ui-library/charting/statistics/Types';
import removeNulls from 'util/removeNulls';
import {JSONValue} from 'util/Types';

import {GetRegressionDataFrames_modelVariablesDataFrame_DataFrameNode as GetRegressionVariablesDataFrame} from './__generated/GetRegressionDataFrames';
import {
  getRegressionModelVariablesAccessors,
  RegressionModelVariablesAccessors,
} from './RegressionModelVariablesAccessors';

export const GET_REGRESSION_DATA_FRAMES_QUERY = gql`
  query GetRegressionDataFrames(
    # We define default stub values for the data frame ids because the regression step's output may not yet exist.
    # In some cases where the output does not yet exist, it's still necessary to perform this query (and to let it
    # error with the "STUB" ids), since querying is performed via hook. Otherwise, we may run into "Cannot update
    # during an existing state transition" errors.
    $modelOutputDataFrameId: StrictString = "STUB"
    $modelVariablesDataFrameId: StrictString = "STUB"
  ) @skipAudit @engComponent(name: DATA_FRAMES) {
    modelOutputDataFrame: dataFrame(id: $modelOutputDataFrameId) {
      ... on DataFrameNode {
        id
        dataFrameJson
        allColumnsJson
      }
      ...InaccessibleInfoGraphQL
    }
    modelVariablesDataFrame: dataFrame(id: $modelVariablesDataFrameId) {
      ... on DataFrameNode {
        id
        dataFrameJson
        allColumnsJson
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

// Keep in sync with IA client output
// Linear regression examples: services/interactive-analysis/tests/common/regression/linear_regression_test.py
// Quadratic regression examples: services/interactive-analysis/tests/common/regression/quadratic_regression_test.py
// 4PL regression examples: services/interactive-analysis/tests/common/regression/four_parameter_logistic_regression_test.py
enum VariableName {
  B0 = 'b0',
  B1 = 'b1',
  B2 = 'b2',
  R2 = 'R2',
  MIN = 'min',
  MAX = 'max',
  INFLECTION_POINT = 'inflection_point',
  HILL_SLOPE = 'hill_slope',
  RMSE = 'RMSE',
}

type RegressionModelVariablesDataFrameModelType = 'LINEAR' | 'QUADRATIC' | 'FOUR_PARAMETER_LOGISTIC';

function getModelType(
  dataFrame: DataFrame,
  modelTypeAccessor: DataFrameValueAccessor<string>
): RegressionModelVariablesDataFrameModelType {
  const modelType = modelTypeAccessor(dataFrame.rows[0]);
  assert(
    ['LINEAR', 'QUADRATIC', 'FOUR_PARAMETER_LOGISTIC'].includes(modelType),
    'Found unexpected model type',
    {modelType}
  );
  assert(
    dataFrame.rows.every((row) => modelType === modelTypeAccessor(row)),
    'Found inconsistent model type in data frame rows'
  );

  return modelType as RegressionModelVariablesDataFrameModelType;
}

export function getPartitions(
  dataFrame: DataFrame,
  partitionAccessor: DataFrameValueAccessor<JSONValue> | null
): Array<JSONValue> {
  if (partitionAccessor == null) {
    return [];
  }

  const partitions = _.unique(dataFrame.rows.map((row) => partitionAccessor(row)));
  return removeNulls(partitions);
}

interface ParameterValue {
  value: number | null;
  confidenceIntervalLowerBound: number | null;
  confidenceIntervalUpperBound: number | null;
  pValue: number | null;
  error: string | null;
}

/** Extract a parameter value from the regression step variables data frame.
 *
 * The parameter value may also have additional information, such as a confidence interval and its p-value.
 * We expect that either the value will be non-null or the row will contain an error message.
 */
function getParameterValue(
  dataFrame: DataFrame,
  accessors: RegressionModelVariablesAccessors,
  partition: JSONValue | null,
  parameterName: string
): ParameterValue {
  const filteredRows = dataFrame.rows.filter(
    (row) => (partition == null || row.partition === partition) && row.parameter === parameterName
  );

  assert(filteredRows.length === 1, 'Expectedly to find exactly one matching row');
  const [row] = filteredRows;

  assert(
    row.value != null || row.error != null,
    'Unexpected null value without error message for parameter',
    {parameterName}
  );

  return {
    value: accessors.valueAccessor(row),
    confidenceIntervalLowerBound: accessors.confidenceIntervalLowerBoundAccessor(row),
    confidenceIntervalUpperBound: accessors.confidenceIntervalUpperBoundAccessor(row),
    pValue: accessors.pValueAccessor != null ? accessors.pValueAccessor(row) : null,
    error: accessors.errorAccessor(row),
  };
}

/** @package */
export function getLinearRegressionAnalyses(
  dataFrame: DataFrame,
  accessors: RegressionModelVariablesAccessors,
  partitions: Array<JSONValue>
): Array<SuccessfulAnalysis<RegressionAnalysis>> {
  return removeNulls(
    (partitions.length > 0 ? partitions : [null]).map((partition) => {
      const B0 = getParameterValue(dataFrame, accessors, partition, VariableName.B0).value;
      const B1 = getParameterValue(dataFrame, accessors, partition, VariableName.B1).value;

      if (B0 == null || B1 == null) {
        return null;
      }

      // If B0 and B1 are non-null, then we expect R2 to be defined
      const R2 = getParameterValue(dataFrame, accessors, partition, VariableName.R2).value!;

      const getEstimatedY = (x: number): number => LinearRegression.estimatePointY(x, {B0, B1});
      return {
        ...(partition != null ? {label: partition} : {}),
        result: {
          fitEquation: LinearRegression.getFitEquation(B0, B1),
          fitOutput: {
            B0,
            B1,
            R2,
          },
          outputValues: [
            {fieldName: 'm', showInPlotHeader: false, value: B1},
            {fieldName: 'b', showInPlotHeader: false, value: B0},
            {fieldName: 'R2', fieldNameDisplayOverride: 'R²', showInPlotHeader: false, value: R2},
          ],
          getEstimatedY,
          tooltipDisclaimer: null,
        },
        status: AnalysisStatus.SUCCESS,
      };
    })
  );
}

/** @package */
export function getQuadraticRegressionAnalyses(
  dataFrame: DataFrame,
  accessors: RegressionModelVariablesAccessors,
  partitions: Array<JSONValue>
): Array<SuccessfulAnalysis<RegressionAnalysis>> {
  // TODO(BNCH-89872): Allow displaying quadratic regressions in charting library
  return removeNulls(
    (partitions.length > 0 ? partitions : [null]).map((partition) => {
      const B0 = getParameterValue(dataFrame, accessors, partition, VariableName.B0).value;
      const B1 = getParameterValue(dataFrame, accessors, partition, VariableName.B1).value;
      const B2 = getParameterValue(dataFrame, accessors, partition, VariableName.B2).value;

      if (B0 == null || B1 == null || B2 == null) {
        return null;
      }

      // If B0, B1, and B2 are non-null, then we expect R2 to be defined
      const R2 = getParameterValue(dataFrame, accessors, partition, VariableName.R2).value!;

      const getEstimatedY = (x: number): number => QuadraticRegression.estimatePointY(x, {B0, B1, B2});
      return {
        ...(partition != null ? {label: partition} : {}),
        result: {
          fitEquation: null,
          fitOutput: {
            B0,
            B1,
            B2,
            R2,
          },
          outputValues: [
            {fieldName: 'b2', showInPlotHeader: false, value: B2},
            {fieldName: 'b1', showInPlotHeader: false, value: B1},
            {fieldName: 'b0', showInPlotHeader: false, value: B0},
            {fieldName: 'R2', fieldNameDisplayOverride: 'R²', showInPlotHeader: false, value: R2},
          ],
          getEstimatedY,
          tooltipDisclaimer: null,
        },
        status: AnalysisStatus.SUCCESS,
      };
    })
  );
}

/** @package */
export function getLogisticRegressionAnalyses(
  dataFrame: DataFrame,
  accessors: RegressionModelVariablesAccessors,
  partitions: Array<JSONValue>
): Array<SuccessfulAnalysis<RegressionAnalysis>> {
  return removeNulls(
    (partitions.length > 0 ? partitions : [null]).map((partition) => {
      const min = getParameterValue(dataFrame, accessors, partition, VariableName.MIN).value;
      const max = getParameterValue(dataFrame, accessors, partition, VariableName.MAX).value;
      const hillSlope = getParameterValue(dataFrame, accessors, partition, VariableName.HILL_SLOPE).value;
      const ic50 = getParameterValue(dataFrame, accessors, partition, VariableName.INFLECTION_POINT).value;

      if (min == null || max == null || hillSlope == null || ic50 == null) {
        return null;
      }

      const logIC50 = Math.log10(ic50);

      // If min, max, hillSlope, and ic50 are non-null, then we expect R2 and RMSE to be defined
      const R2 = getParameterValue(dataFrame, accessors, partition, VariableName.R2).value!;
      const RMSE = getParameterValue(dataFrame, accessors, partition, VariableName.RMSE).value!;
      const getEstimatedY = (x: number): number =>
        LogisticRegression.estimatePointY(x, {min, max, logIC50, hillSlope});

      return {
        ...(partition != null ? {label: partition} : {}),
        result: {
          fitEquation: null,
          fitOutput: {
            min,
            max,
            hillSlope,
            ic50,
            logIC50,
            R2,
            RMSE,
          },
          outputValues: [
            {fieldName: 'IC50', showInPlotHeader: false, value: ic50},
            {fieldName: 'Slope', showInPlotHeader: false, value: hillSlope},
            {fieldName: 'Min', showInPlotHeader: false, value: min},
            {fieldName: 'Max', showInPlotHeader: false, value: max},
            {fieldName: 'R2', fieldNameDisplayOverride: 'R²', showInPlotHeader: false, value: R2},
          ],
          getEstimatedY,
          tooltipDisclaimer: null,
        },
        status: AnalysisStatus.SUCCESS,
      };
    })
  );
}

const MODEL_TYPE_TO_REGRESSION_ANALYSES_FN: {
  [key in RegressionModelVariablesDataFrameModelType]: (
    dataFrame: DataFrame,
    accessors: RegressionModelVariablesAccessors,
    partitions: Array<JSONValue>
  ) => Array<SuccessfulAnalysis<RegressionAnalysis>>;
} = {
  LINEAR: getLinearRegressionAnalyses,
  QUADRATIC: getQuadraticRegressionAnalyses,
  FOUR_PARAMETER_LOGISTIC: getLogisticRegressionAnalyses,
};

/**
 * Translate the regression model variables data frame into graph-able regression analyses.
 *
 * @param modelVariablesDataFrame Expected columns:
 *   - 'model_type'
 *   - 'partition'
 *   - 'parameter'
 *   - 'value'
 *   - 'confidence_interval_upper_bound'
 *   - 'p_value' (optional)
 *   - 'error'
 */
export default function getPersistedRegressionAnalyses(
  modelVariablesDataFrame: GetRegressionVariablesDataFrame
): Array<SuccessfulAnalysis<RegressionAnalysis>> {
  const dataFrame = modelVariablesDataFrame.dataFrameJson as DataFrame;
  const accessors = getRegressionModelVariablesAccessors(dataFrame);

  const modelType = getModelType(dataFrame, accessors.modelTypeAccessor);
  const partitions = getPartitions(dataFrame, accessors.partitionAccessor ?? null);

  return MODEL_TYPE_TO_REGRESSION_ANALYSES_FN[modelType](dataFrame, accessors, partitions);
}
